import billingApi from '@/lib/billingApi'

export type ServerCancellationReceipt = {
  accepted: true
  status: 'cancelled'
  server_uuid: string
  operation_id: string
  cancelled_at: string
  billing_cutoff_at: string
  cleanup_status: 'pending' | 'processing' | 'completed'
  cleanup_phase?: 'freezing' | 'preserving' | 'deleting_archives' | 'deleting_server' | 'cleanup' | 'completed'
}

export async function getServerCancellationStatus(receipt: ServerCancellationReceipt): Promise<ServerCancellationReceipt> {
  const response = await billingApi.get<unknown>(`/hoard-api/subscription/cancel/status/${encodeURIComponent(receipt.operation_id)}`)
  const latest = requireServerCancellationReceipt(response.data, receipt.server_uuid)
  if (latest.operation_id !== receipt.operation_id || latest.billing_cutoff_at !== receipt.billing_cutoff_at
    || latest.cancelled_at !== receipt.cancelled_at) {
    throw new Error('Cancellation status does not match the saved receipt.')
  }
  return latest
}

export function requireServerCancellationReceipt(value: unknown, expectedServerUuid?: string): ServerCancellationReceipt {
  const receipt = value as Partial<ServerCancellationReceipt> | null
  if (!receipt || receipt.accepted !== true || receipt.status !== 'cancelled'
    || typeof receipt.server_uuid !== 'string' || !/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(receipt.server_uuid)
    || (expectedServerUuid && receipt.server_uuid !== expectedServerUuid)
    || typeof receipt.operation_id !== 'string' || !receipt.operation_id.trim()
    || typeof receipt.cancelled_at !== 'string' || !Number.isFinite(Date.parse(receipt.cancelled_at))
    || typeof receipt.billing_cutoff_at !== 'string' || !Number.isFinite(Date.parse(receipt.billing_cutoff_at))
    || Date.parse(receipt.billing_cutoff_at) !== Date.parse(receipt.cancelled_at)
    || !['pending', 'processing', 'completed'].includes(receipt.cleanup_status || '')) {
    throw new Error('Cancellation could not be confirmed. Please check again before submitting another request.')
  }
  // Optional worker detail cannot invalidate a confirmed billing cutoff. Ignore
  // unknown phases from newer servers rather than inventing checked steps.
  const { cleanup_phase, ...confirmed } = receipt as ServerCancellationReceipt
  const knownPhase = ['freezing', 'preserving', 'deleting_archives', 'deleting_server', 'cleanup', 'completed'].includes(cleanup_phase || '')
  return {
    ...confirmed,
    ...(knownPhase && (cleanup_phase !== 'completed' || receipt.cleanup_status === 'completed') ? { cleanup_phase } : {}),
  }
}

export async function getServerCancellations(): Promise<ServerCancellationReceipt[]> {
  const response = await billingApi.get<unknown>('/hoard-api/subscription/cancel/status')
  if (!Array.isArray(response.data)) throw new Error('Server cancellation status is unavailable.')
  // This endpoint also contains older backup-disposition records. Only an
  // explicit acceptance receipt establishes that a server was cancelled.
  return response.data.filter(value => value?.accepted === true).map(value => requireServerCancellationReceipt(value))
}

export function cancellationForServer(receipts: ServerCancellationReceipt[], uuid: string): ServerCancellationReceipt | undefined {
  return receipts.find(receipt => receipt.server_uuid === uuid
    || (/^[0-9a-f]{8}$/i.test(uuid) && receipt.server_uuid.slice(0, 8) === uuid))
}

export function serverCancellationMessage(receipt: ServerCancellationReceipt): string {
  return receipt.cleanup_status === 'completed'
    ? 'Server cancelled. Server cleanup is complete.'
    : 'Server cancelled. No additional server usage or renewal charges will accrue. Cleanup continues automatically.'
}

export class ServerCancelledError extends Error {
  constructor(public readonly receipt: ServerCancellationReceipt) {
    super(serverCancellationMessage(receipt))
    this.name = 'ServerCancelledError'
  }
}
